import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '@/lib/firebase';
import { collection, query, where, getDocs, doc, setDoc, serverTimestamp, updateDoc, documentId, addDoc } from 'firebase/firestore';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/lib/logger';

const TenantContext = createContext({
    currentTenant: null,
    tenants: [],
    loading: true,
    switchTenant: () => {},
    createTenant: () => {},
    updateTenant: () => {}
});

const SUPERADMIN_EMAILS = [
    'sanjay@the2conf.com', 
    'isanjaydev@gmail.com', 
    'admin@oblique.com', 
    'business.eleventh@gmail.com',
    'eleventh',
    'nischal',
    'scale',
    'test'
];

export const isSuperAdminEmail = (email) => {
    if (!email) return false;
    const lower = email.toLowerCase();
    return SUPERADMIN_EMAILS.some(s => lower === s || lower.includes(s));
};

export function TenantProvider({ children }) {
    const { user, userRole } = useAuth();
    const [currentTenant, setCurrentTenant] = useState(() => {
        const storedId = localStorage.getItem('oblique_tenant_id');
        return storedId ? { id: storedId, name: 'Loading...' } : null;
    });
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const initTenant = async () => {
            if (!user) {
                setCurrentTenant(null);
                setTenants([]);
                setLoading(false);
                return;
            }

            try {
                const emailLower = user.email.toLowerCase();
                const isSuper = userRole === 'superadmin' || isSuperAdminEmail(emailLower);
                
                // 1. Fetch tenant_users mapping for this user
                const tuQuery = query(collection(db, 'tenant_users'), where('email', '==', emailLower));
                const tuSnap = await getDocs(tuQuery);

                let userTenants = [];
                
                if (tuSnap.empty && isSuper) {
                    // MIGRATION / BOOTSTRAP LOGIC (Runs once for superadmins if no tenant exists)
                    logger.info("Bootstrapping Default Tenant for Superadmin...");
                    const defaultTenantId = 'tenant-scale-agency';
                    
                    // Create Tenant
                    await setDoc(doc(db, 'tenants', defaultTenantId), {
                        name: 'The Scale Agency',
                        domain: 'thescaleagency.com',
                        logoUrl: '/Workspaces/tsa_mark.svg',
                        createdAt: serverTimestamp()
                    });

                    // Create tenant_user mapping
                    await setDoc(doc(db, 'tenant_users', `${defaultTenantId}_${emailLower}`), {
                        tenantId: defaultTenantId,
                        email: emailLower,
                        role: 'superadmin',
                        departments: ['both']
                    });

                    // Migrate all existing companies to this default tenant safely
                    try {
                        const compSnap = await getDocs(collection(db, 'companies'));
                        const updates = compSnap.docs.map(d => updateDoc(doc(db, 'companies', d.id), { tenantId: defaultTenantId }));
                        await Promise.all(updates);
                        logger.info("Migrated all companies to default tenant.");
                    } catch (e) {
                        logger.warn("Could not auto-migrate legacy companies (likely permission denied). Falling back to legacy loading.", e);
                    }

                    userTenants = [{ id: defaultTenantId, name: 'The Scale Agency', domain: 'thescaleagency.com', logoUrl: '/Workspaces/tsa_mark.svg' }];
                } else if (!tuSnap.empty) {
                    // Normal Flow: Load authorized tenants
                    const tenantIds = tuSnap.docs.map(d => d.data().tenantId);
                    
                    if (tenantIds.length > 0) {
                        // Fetch the actual tenant docs safely
                        try {
                            const chunks = [];
                            for (let i = 0; i < tenantIds.length; i += 30) chunks.push(tenantIds.slice(i, i + 30));
                            
                            const results = await Promise.all(chunks.map(chunk => 
                                getDocs(query(collection(db, 'tenants'), where(documentId(), 'in', chunk)))
                            ));
                            
                            userTenants = results.flatMap(snap => snap.docs.map(d => {
                                const data = d.data();
                                const isScaleAgency = d.id === 'tenant-scale-agency' || data.domain?.includes('scale');
                                return {
                                    id: d.id,
                                    ...data,
                                    name: (data.name && data.name !== 'Primary Workspace' && data.name !== 'Primary Organization') 
                                        ? data.name 
                                        : (isScaleAgency ? 'The Scale Agency' : data.name),
                                    domain: data.domain || (isScaleAgency ? 'thescaleagency.com' : '')
                                };
                            }));
                        } catch (e) {
                            logger.error("Failed to query tenants collection:", e);
                        }
                    }
                }

                // Failsafe: if we still have no tenants, default to The Scale Agency
                if (userTenants.length === 0) {
                    userTenants = [{ id: 'tenant-scale-agency', name: 'The Scale Agency', domain: 'thescaleagency.com', logoUrl: '/Workspaces/tsa_mark.svg' }];
                }

                setTenants(userTenants);

                if (userTenants.length > 0) {
                    const storedId = localStorage.getItem('oblique_tenant_id');
                    const active = userTenants.find(t => t.id === storedId) || userTenants[0];
                    setCurrentTenant(active);
                    localStorage.setItem('oblique_tenant_id', active.id);
                } else {
                    const defaultTenant = { id: 'tenant-scale-agency', name: 'The Scale Agency', domain: 'thescaleagency.com', logoUrl: '/Workspaces/tsa_mark.svg' };
                    setCurrentTenant(defaultTenant);
                    localStorage.setItem('oblique_tenant_id', defaultTenant.id);
                }

            } catch (err) {
                logger.error("Error initializing TenantContext. Activating primary fallback mode.", err);
                const fallbackTenant = { id: 'tenant-scale-agency', name: 'The Scale Agency', domain: 'thescaleagency.com', logoUrl: '/Workspaces/tsa_mark.svg' };
                setTenants([fallbackTenant]);
                setCurrentTenant(fallbackTenant);
                localStorage.setItem('oblique_tenant_id', fallbackTenant.id);
            } finally {
                setLoading(false);
            }
        };

        initTenant();
    }, [user, userRole]);

    const switchTenant = (tenantId) => {
        const found = tenants.find(t => t.id === tenantId);
        if (found) {
            setCurrentTenant(found);
            localStorage.setItem('oblique_tenant_id', tenantId);
            // Optionally force reload to clear downstream states
            window.location.reload(); 
        }
    };

    const createTenant = async (data) => {
        const emailLower = user ? user.email.toLowerCase() : '';
        const isSuper = userRole === 'superadmin' || isSuperAdminEmail(emailLower);
        if (!user || !isSuper) throw new Error("Only superadmins can create a new Tenant.");
        
        try {
            const { name, domain, description, lightLogoUrl, darkLogoUrl, industry } = data;
            const tenantId = `tenant-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

            // 1. Create Tenant
            await setDoc(doc(db, 'tenants', tenantId), {
                name,
                domain: domain || '',
                description: description || '',
                lightLogoUrl: lightLogoUrl || '',
                darkLogoUrl: darkLogoUrl || '',
                industry: industry || '',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });

            // 2. Create corresponding holding company in companies collection for immediate sync
            try {
                await addDoc(collection(db, 'companies'), {
                    name,
                    domain: domain || '',
                    tenantId,
                    type: 'holding',
                    status: 'active',
                    logoUrl: lightLogoUrl || darkLogoUrl || '',
                    lightLogoUrl: lightLogoUrl || '',
                    darkLogoUrl: darkLogoUrl || '',
                    createdBy: emailLower,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                });
            } catch (compErr) {
                logger.warn("Could not auto-create company for new tenant:", compErr);
            }

            // 3. Create User Mapping
            await setDoc(doc(db, 'tenant_users', `${tenantId}_${emailLower}`), {
                tenantId,
                email: emailLower,
                role: 'superadmin',
                departments: ['both'],
                createdAt: serverTimestamp()
            });

            const newTenant = { id: tenantId, ...data };
            
            // Update local state
            setTenants(prev => [...prev, newTenant]);
            switchTenant(tenantId);
            
            return newTenant;
        } catch (err) {
            logger.error("Failed to create tenant", err);
            throw err;
        }
    };

    const updateTenant = async (tenantId, updates) => {
        try {
            await updateDoc(doc(db, 'tenants', tenantId), {
                ...updates,
                updatedAt: serverTimestamp()
            });

            // Also update any matching companies for this tenant in Firestore
            try {
                const compQuery = query(collection(db, 'companies'), where('tenantId', '==', tenantId));
                const compSnap = await getDocs(compQuery);
                const companyUpdates = compSnap.docs.map(d => updateDoc(doc(db, 'companies', d.id), {
                    ...(updates.name ? { name: updates.name } : {}),
                    ...(updates.domain ? { domain: updates.domain } : {}),
                    ...(updates.lightLogoUrl !== undefined ? { lightLogoUrl: updates.lightLogoUrl } : {}),
                    ...(updates.darkLogoUrl !== undefined ? { darkLogoUrl: updates.darkLogoUrl } : {}),
                    ...(updates.lightLogoUrl || updates.darkLogoUrl ? { logoUrl: updates.lightLogoUrl || updates.darkLogoUrl } : {}),
                    updatedAt: serverTimestamp()
                }));
                await Promise.all(companyUpdates);
            } catch (syncErr) {
                logger.warn("Could not sync tenant updates to companies collection:", syncErr);
            }

            setTenants(prev => prev.map(t => t.id === tenantId ? { ...t, ...updates } : t));
            if (currentTenant?.id === tenantId) {
                setCurrentTenant(prev => ({ ...prev, ...updates }));
            }
        } catch (err) {
            logger.error("Failed to update tenant:", err);
            throw err;
        }
    };

    return (
        <TenantContext.Provider value={{ currentTenant, tenants, loading, switchTenant, createTenant, updateTenant }}>
            {children}
        </TenantContext.Provider>
    );
}

export const useTenant = () => useContext(TenantContext);
